import { setupListeners } from '@reduxjs/toolkit/query';
import moment from 'moment-timezone';
import { hydrateRoot } from 'react-dom/client';

import App from '@app';
import { makeRouteResolveContext } from '@components/router';

import history from './history';
import router, { updateRouterContext } from './router';
import buildStore from './store';

// Switch off the native scroll restoration behavior and handle it manually
// https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
if (window.history && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const appData = {
  apiUrl: window.App.apiUrl || false,
  uiVersion: window.App.uiVersion || false,
  engineVersion: window.App.engineVersion || false,
  dbVersion: window.App.dbVersion || false,
  isAppConnectionHealthy: window.App.isAppConnectionHealthy || false,
  isAuthenticated: !!window.App.isAuthenticated,
  isProduction: window.App.isProduction || false,
  isEngineHealthy: window.App.isEngineHealthy || false,
  isAppDBHealthy: window.App.isAppDBHealthy || false,
  isEngineVersionGood: window.App.isEngineVersionGood || false,
  isLicenseGood: window.App.isLicenseGood || false,
  isReportsEnabledAndActive: window.App.isReportsEnabledAndActive || {
    enabled: false,
    active: false,
  },
  account: window.App.account || false,
  isLdapEnabled: window.App.isLdapEnabled || false,
  isSsoAuthOnly: window.App.isSsoAuthOnly || false,
  customLinks: window.App.customLinks || false,
  whatsNew: window.App.whatsNew || false,
  isWebSocketOnly: window.App.isWebSocketOnly || false,
  customMessage: window.App.customMessage || false,
  customBanners: window.App.customBanners || null,
  enrichInventoryView: window.App.enrichInventoryView ?? true,
  featureFlags: window.App.featureFlags,
  anchoreInternalDeployment: window.App.anchoreInternalDeployment ?? false,
  entitlements: window.App.entitlements ?? {},
  uiCommitSha: window.App.uiCommitSha || null,
  uiImageBuildTimestamp: window.App.uiImageBuildTimestamp || null,
};

/* eslint-disable no-underscore-dangle */
const initialStyles = window.__INITIAL_STYLES__;
const serverState = window.__PRELOADED_STATE__;
/* eslint-enable */

const store = buildStore(__DEV__, serverState);

// Enable RTK Query's automatic refetching on focus/reconnect
// This is disabled by default and must be explicitly enabled in the hook
setupListeners(store.dispatch);

updateRouterContext({
  featureFlags: appData.featureFlags,
  entitlements: appData.entitlements,
});

let root;

(async () => {
  if (root) return;

  // Resolve the initial route to hydrate the app with.
  // This is expected to match what was resolved on the server.
  const initialRouteContext = await router.resolve(
    makeRouteResolveContext(history.location),
  );

  root = hydrateRoot(
    document,
    <App
      data={appData}
      store={store}
      serverState={serverState}
      initialStyles={initialStyles}
      initialRouteContext={initialRouteContext}
    />,
    {
      onRecoverableError: () => {
        // This happens when there is an error during hydration that the client
        // can recover from. Trigger a re-render with a flag to allow components
        // to handle the scenario if necessary, such as retaining styles from
        // the server.
        root.render(
          <App
            data={appData}
            store={store}
            initialStyles={initialStyles}
            initialRouteContext={initialRouteContext}
            isClientRecovery
          />,
        );
      },
    },
  );
})();

if (module.hot) {
  // When the router module has HMR updates, re-render the app
  module.hot.accept('./router', () => {
    if (!root) return;
    root.render(<App data={appData} store={store} />);
  });
}

console.info(
  `%c
                    ..--:///:--..
                \`.:+++++++++++++++:.\`
              \`:+++++++++++++++++++++:\`
            .+++++++/::.....::/+++++++.
            -++++++/-           -/++++++.
            /+++++/\`             \`/++++++
            \`:///:                \`++++++-
                                  ++++++-
                                \`.++++++-   ╭────────────────────────────────────╮
                          \`\`..://++++++++-  │ Anchore Enterprise Client: v${
                            window.App.uiVersion
                          } │
                  \`\`..:://+++++++++++++++-  │ Commit SHA: ${
                    window.App.uiCommitSha
                      ? `${window.App.uiCommitSha.substring(0, 7)}               `
                      : '______________________'
                  } │
              .-/+++++++++++++++//++++++-   │ Build: ${
                window.App.uiImageBuildTimestamp
                  ? `${window.App.uiImageBuildTimestamp}       `
                  : '___________________________'
              } │
            -/+++++++++++/::..\`\`  ++++++-   ╰────────────────────────────────────╯
            :+++++++/-..\`\`         ++++++-
          .++++++:\`               ++++++-
          -++++++                 ++++++-
          -++++++                 ++++++-
          \`++++++/\`               ++++++-
            -++++++/:\`             ./++++-
            ./+++++++///::\`        \`:+++-
              ./+++++++++++          \`.:.
                \`-:/++++++/
                      \`.... © Anchore Inc. ${moment().year()}. All Rights Reserved.

`,
  'color: rgb(54, 130, 224);text-shadow: 1px 1px rgba(0, 0, 0, 0.125);',
);
